import React, { useState, useEffect } from "react";
import { AddCleaningServiceSheet } from "@/pages/bids/AddCleaningServiceSheet";
import { AddFloorcareServiceSheet } from "@/pages/bids/AddFloorcareServiceSheet";
import { ViewCleaningServiceSheet } from "@/pages/bids/ViewCleaningServiceSheet";
import { EditCleaningServiceSheet } from "@/pages/bids/EditCleaningServiceSheet";
import { EditFloorcareServiceSheet } from "@/pages/bids/EditFloorcareServiceSheet";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Save, FileText, Clock, Users, Building2, Edit } from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";

interface ServiceItem {
  id: string;
  type: "cleaning" | "floorcare"; // Use literal union type instead of string
  name: string;
  frequency: string;
  squareFootage: number;
  productionRate: number;
  laborRate: number;
  hours: number;
  price: number;
  description?: string;
  serviceDays?: string[];
  pricingMethod?: "hourly" | "squareFootage";
  overheadPercentage?: number;
  profitMargin?: number;
  ratePerSqFt?: number; // Add ratePerSqFt field
}

interface PricingCalculatorProps {
  bidId: Id<"bids">; // Properly typed as Id<"bids">
}

export function PricingCalculator({ bidId }: PricingCalculatorProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"cleaning" | "floorcare">("cleaning");
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [isAddServiceSheetOpen, setIsAddServiceSheetOpen] = useState(false);
  const [isAddFloorcareSheetOpen, setIsAddFloorcareSheetOpen] = useState(false);
  const [isAddCleaningServiceSheetOpen, setIsAddCleaningServiceSheetOpen] = useState(false);
  const [isViewServiceSheetOpen, setIsViewServiceSheetOpen] = useState(false);
  const [isEditCleaningServiceSheetOpen, setIsEditCleaningServiceSheetOpen] = useState(false);
  const [isEditFloorcareServiceSheetOpen, setIsEditFloorcareServiceSheetOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(null);
  const [newService, setNewService] = useState<Partial<ServiceItem>>({
    type: "cleaning",
    name: "",
    frequency: "daily",
    squareFootage: 0,
    productionRate: 0,
    laborRate: 25,
  });

  // Fetch services for this bid
  const bidServices = useQuery(api.bidPricing.getByBidId, { bidId });

  // Fetch bid summary
  const bidSummary = useQuery(api.bidPricing.getBidSummary, { bidId });

  // Process the services data when it's loaded
  useEffect(() => {
    if (bidServices && bidServices.length > 0) {
      // Convert services to our ServiceItem format
      const allServices: ServiceItem[] = [];

      bidServices.forEach((service: any) => {
        allServices.push({
          id: service._id.toString(),
          type: service.type as "cleaning" | "floorcare",
          name: service.name,
          frequency: service.frequency,
          squareFootage: service.squareFootage,
          productionRate: service.productionRate,
          laborRate: service.laborRate,
          hours: service.hours,
          price: service.price,
          description: service.description || "",
          serviceDays: service.serviceDays || [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
          ],
          pricingMethod: service.pricingMethod as "hourly" | "squareFootage",
          overheadPercentage: service.overheadPercentage,
          profitMargin: service.profitMargin,
          ratePerSqFt: service.ratePerSqFt, // Include ratePerSqFt field
        });
      });

      console.log("Loaded services from database:", allServices);

      // Debug ratePerSqFt values
      allServices.forEach((service) => {
        console.log(`Service ${service.name} ratePerSqFt:`, {
          value: service.ratePerSqFt,
          type: typeof service.ratePerSqFt,
        });
      });
      setServices(allServices);
    }
  }, [bidServices]);

  const frequencyOptions = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "biweekly", label: "Bi-Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "annually", label: "Annually" },
  ];

  const calculateHours = (squareFootage: number, productionRate: number) => {
    if (!squareFootage || !productionRate) return 0;
    return squareFootage / productionRate;
  };

  const calculateCost = (hours: number, laborRate: number) => {
    return hours * laborRate;
  };

  const calculateMonthlyFrequency = (frequency: string) => {
    // For converting any frequency to number of services per month
    if (frequency.includes("per_week")) {
      const timesPerWeek = parseInt(frequency.split("_")[0]);
      return timesPerWeek * 4.33; // Average weeks per month
    } else if (frequency.includes("per_month")) {
      const timesPerMonth = parseInt(frequency.split("_")[0]);
      return timesPerMonth;
    }

    switch (frequency) {
      case "daily":
        return 22; // business days in a month
      case "weekly":
        return 4.33; // weeks in a month
      case "biweekly":
        return 2.17; // bi-weekly in a month
      case "monthly":
        return 1; // once per month
      case "quarterly":
        return 4 / 12; // 4 services per year / 12 months
      case "semiannually":
        return 2 / 12; // 2 services per year / 12 months
      case "annually":
        return 1 / 12; // 1 service per year / 12 months
      default:
        return 1;
    }
  };

  // Helper function to format frequency for display
  const formatFrequencyForDisplay = (frequency: string) => {
    if (frequency.includes("_per_week")) {
      const timesPerWeek = parseInt(frequency.split("_")[0]);
      return timesPerWeek === 1 ? "Weekly (1 time per week)" : `${timesPerWeek} Days per Week`;
    } else if (frequency.includes("_per_month")) {
      const timesPerMonth = parseInt(frequency.split("_")[0]);
      return timesPerMonth === 1 ? "Monthly" : `${timesPerMonth} Times per Month`;
    } else if (frequency === "quarterly") {
      return "Quarterly";
    } else if (frequency === "semiannually") {
      return "Semi-Annually";
    } else if (frequency === "annually") {
      return "Annually";
    } else if (frequency === "weekly") {
      return "Weekly (1 time per week)";
    } else if (frequency === "monthly") {
      return "Monthly";
    }
    return frequency;
  };

  const handleAddService = () => {
    if (!newService.name || !newService.squareFootage || !newService.productionRate) {
      return;
    }

    const hours = calculateHours(newService.squareFootage || 0, newService.productionRate || 0);
    const price = calculateCost(hours, newService.laborRate || 25);

    const service: ServiceItem = {
      id: Date.now().toString(),
      type: newService.type || "cleaning",
      name: newService.name || "",
      frequency: newService.frequency || "daily",
      squareFootage: newService.squareFootage || 0,
      productionRate: newService.productionRate || 0,
      laborRate: newService.laborRate || 25,
      hours,
      price,
    };

    setServices([...services, service]);
    setNewService({
      type: newService.type,
      name: "",
      frequency: "daily",
      squareFootage: 0,
      productionRate: 0,
      laborRate: 25,
    });
  };

  const createService = useMutation(api.bidPricing.create);
  const removeServiceMutation = useMutation(api.bidPricing.removeService);
  const updateServiceMutation = useMutation(api.bidPricing.update);

  const handleAddServiceFromSheet = (serviceData: any) => {
    console.log(
      "PricingCalculator: handleAddServiceFromSheet called with data:",
      JSON.stringify(serviceData, null, 2)
    );

    // Check if required fields are present
    if (!serviceData || !serviceData.name) {
      console.error("PricingCalculator: Invalid service data received:", serviceData);
      toast.error("Invalid service data received");
      return;
    }

    let hours = 0;
    let price = 0;

    if (serviceData.pricingMethod === "hourly") {
      // For hourly pricing, use the estimatedHours from the form
      hours = serviceData.estimatedHours || 0;
      price = serviceData.totalPrice;
      console.log("PricingCalculator: Hourly pricing - hours:", hours, "price:", price);
    } else {
      // For square footage method
      const productionRate = serviceData.productionRate || serviceData.squareFootage / 4; // Use provided rate or default
      hours = serviceData.squareFootage / productionRate;
      price = serviceData.totalPrice;
      console.log("PricingCalculator: Square footage pricing - hours:", hours, "price:", price);
    }

    // Ensure type is set correctly as a literal
    // Force the type to be either "cleaning" or "floorcare"
    const serviceType: "cleaning" | "floorcare" =
      serviceData.type === "floorcare" ? "floorcare" : "cleaning";
    console.log("PricingCalculator: Service type:", serviceType);

    // Log the type of the serviceType variable to ensure it's a string literal
    console.log("PricingCalculator: Type of serviceType:", typeof serviceType);

    const service: ServiceItem = {
      id: Date.now().toString(),
      type: serviceType, // Use the explicit type from serviceData
      name: serviceData.name,
      frequency: serviceData.frequency,
      // Always save squareFootage if provided, regardless of pricing method
      squareFootage: serviceData.squareFootage || 0,
      // Always save productionRate if provided, regardless of pricing method
      productionRate: serviceData.productionRate || 0,
      laborRate: serviceData.pricingMethod === "hourly" ? serviceData.hourlyRate : price / hours,
      hours,
      price,
      description: serviceData.description || "",
      serviceDays: serviceData.serviceDays || [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
      ],
      pricingMethod: serviceData.pricingMethod, // Explicitly set the pricing method
      overheadPercentage: serviceData.overheadPercentage,
      profitMargin: serviceData.profitMargin,
      ratePerSqFt: serviceData.ratePerSqFt || 0, // Include ratePerSqFt field
    };

    console.log("PricingCalculator: Created service item:", JSON.stringify(service, null, 2));

    // Update local state
    console.log("PricingCalculator: Updating local state with new service");
    setServices([...services, service]);
    console.log("PricingCalculator: Local state updated");

    // Add Convex mutation after the local state update
    try {
      console.log("PricingCalculator: Preparing Convex payload");

      // Create a strongly typed payload
      const serviceDataPayload = {
        name: service.name,
        description: service.description,
        frequency: service.frequency,
        serviceDays: service.serviceDays,
        pricingMethod: serviceData.pricingMethod || "hourly",
        hourlyRate: service.laborRate,
        // Ensure estimatedHours is correctly set
        estimatedHours:
          serviceData.pricingMethod === "hourly"
            ? serviceData.estimatedHours || service.hours
            : service.hours,
        // Always include squareFootage and productionRate regardless of pricing method
        squareFootage: serviceData.squareFootage || service.squareFootage,
        ratePerSqFt: serviceData.ratePerSqFt || 0, // Add ratePerSqFt field
        productionRate: serviceData.productionRate || service.productionRate,
        overheadPercentage: serviceData.overheadPercentage || 0,
        profitMargin: serviceData.profitMargin || 0,
        totalPrice: service.price,
        type: serviceType, // Use the explicit type variable
      };

      // Log the ratePerSqFt value for debugging
      console.log("PricingCalculator: ratePerSqFt in payload:", serviceDataPayload.ratePerSqFt);

      // Log the estimated hours to verify
      console.log(
        "PricingCalculator: Estimated hours in payload:",
        serviceDataPayload.estimatedHours
      );

      // Log the payload to verify the type field
      console.log(
        "PricingCalculator: Service data payload:",
        JSON.stringify(serviceDataPayload, null, 2)
      );
      console.log("PricingCalculator: Type field in payload:", serviceDataPayload.type);
      console.log("PricingCalculator: Type of type field:", typeof serviceDataPayload.type);

      const convexPayload = {
        bidId, // bidId is now properly typed as Id<"bids">
        serviceData: serviceDataPayload,
      };

      console.log("PricingCalculator: Convex payload:", JSON.stringify(convexPayload, null, 2));
      console.log("PricingCalculator: Calling createService mutation");

      createService(convexPayload);
      console.log("PricingCalculator: createService mutation called");

      toast.success("Service added successfully");
    } catch (err) {
      console.error("PricingCalculator: Error saving service to Convex:", err);
      toast.error("Failed to save service to database");
    }
  };

  const handleRemoveService = async (id: string) => {
    try {
      // First update the local state for immediate UI feedback
      setServices(services.filter((service) => service.id !== id));

      // Then delete from Convex
      await removeServiceMutation({
        bidId,
        serviceId: id,
      });

      toast.success("Service removed successfully");
    } catch (error) {
      console.error("Error removing service:", error);
      toast.error("Failed to remove service from database");

      // Refresh the services list to ensure UI is in sync with database
      if (bidServices && bidServices.length > 0) {
        const allServices: ServiceItem[] = [];
        bidServices.forEach((service: any) => {
          allServices.push({
            id: service._id.toString(),
            type: service.type as "cleaning" | "floorcare",
            name: service.name,
            frequency: service.frequency,
            squareFootage: service.squareFootage,
            productionRate: service.productionRate,
            laborRate: service.laborRate,
            hours: service.hours,
            price: service.price,
            description: service.description || "",
            serviceDays: service.serviceDays || [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
            ],
            pricingMethod: service.pricingMethod as "hourly" | "squareFootage",
            overheadPercentage: service.overheadPercentage,
            profitMargin: service.profitMargin,
            ratePerSqFt: service.ratePerSqFt, // Include ratePerSqFt field
          });
        });
        setServices(allServices);
      }
    }
  };

  const handleUpdateServiceFromSheet = async (serviceData: any) => {
    try {
      if (!selectedService) return;

      console.log("Updating service with data:", JSON.stringify(serviceData, null, 2));
      console.log("ratePerSqFt in serviceData:", serviceData.ratePerSqFt);
      console.log("ratePerSqFt type:", typeof serviceData.ratePerSqFt);

      // Calculate hours and price based on pricing method
      let hours = 0;
      let price = 0;

      if (serviceData.pricingMethod === "hourly") {
        hours = serviceData.estimatedHours || 0;
        price = serviceData.totalPrice;
      } else {
        const productionRate = serviceData.productionRate || serviceData.squareFootage / 4;
        hours = serviceData.squareFootage / productionRate;
        price = serviceData.totalPrice;
      }

      // Create updated service object for local state
      const updatedService: ServiceItem = {
        ...selectedService,
        name: serviceData.name,
        description: serviceData.description || "",
        frequency: serviceData.frequency,
        squareFootage: serviceData.squareFootage || 0,
        productionRate: serviceData.productionRate || 0,
        laborRate: serviceData.pricingMethod === "hourly" ? serviceData.hourlyRate : price / hours,
        hours,
        price,
        serviceDays: serviceData.serviceDays || [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
        ],
        pricingMethod: serviceData.pricingMethod,
        overheadPercentage: serviceData.overheadPercentage,
        profitMargin: serviceData.profitMargin,
        ratePerSqFt: serviceData.ratePerSqFt,
      };

      // Update local state first for immediate UI feedback
      setServices(
        services.map((service) => (service.id === selectedService.id ? updatedService : service))
      );

      // Prepare the service data payload
      const updatePayload = {
        name: serviceData.name,
        description: serviceData.description || "",
        frequency: serviceData.frequency,
        serviceDays: serviceData.serviceDays || [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
        ],
        pricingMethod: serviceData.pricingMethod,
        hourlyRate: serviceData.hourlyRate,
        estimatedHours: serviceData.estimatedHours,
        squareFootage: serviceData.squareFootage,
        ratePerSqFt: serviceData.ratePerSqFt,
        productionRate: serviceData.productionRate,
        overheadPercentage: serviceData.overheadPercentage || 0,
        profitMargin: serviceData.profitMargin || 0,
        totalPrice: serviceData.totalPrice,
        type: selectedService.type,
      };

      // Log the payload for debugging
      console.log("Update payload being sent to Convex:", JSON.stringify(updatePayload, null, 2));
      console.log("ratePerSqFt in update payload:", updatePayload.ratePerSqFt);
      console.log("ratePerSqFt type in update payload:", typeof updatePayload.ratePerSqFt);

      // Then update in Convex
      await updateServiceMutation({
        id: selectedService.id as Id<"bidPricing">,
        serviceData: updatePayload,
      });

      toast.success("Service updated successfully");
    } catch (error) {
      console.error("Error updating service:", error);
      toast.error("Failed to update service in database");

      // Refresh the services list to ensure UI is in sync with database
      if (bidServices && bidServices.length > 0) {
        const allServices: ServiceItem[] = [];
        bidServices.forEach((service: any) => {
          allServices.push({
            id: service._id.toString(),
            type: service.type as "cleaning" | "floorcare",
            name: service.name,
            frequency: service.frequency,
            squareFootage: service.squareFootage,
            productionRate: service.productionRate,
            laborRate: service.laborRate,
            hours: service.hours,
            price: service.price,
            description: service.description || "",
            serviceDays: service.serviceDays || [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
            ],
            pricingMethod: service.pricingMethod as "hourly" | "squareFootage",
            overheadPercentage: service.overheadPercentage,
            profitMargin: service.profitMargin,
            ratePerSqFt: service.ratePerSqFt, // Include ratePerSqFt field
          });
        });
        setServices(allServices);
      }
    }
  };

  const totalCleaningPrice = services
    .filter((service) => service.type === "cleaning")
    .reduce((total, service) => total + service.price, 0);

  const totalFloorcarePrice = services
    .filter((service) => service.type === "floorcare")
    .reduce((total, service) => total + service.price, 0);

  // Calculate the recommended prices directly from the total prices
  const recommendedMonthlyPrice = totalCleaningPrice + totalFloorcarePrice;
  const recommendedAnnualPrice = recommendedMonthlyPrice * 12;

  return (
    <div className="bg-white p-6">
      {isAddCleaningServiceSheetOpen && (
        <AddCleaningServiceSheet
          isOpen={isAddCleaningServiceSheetOpen}
          onClose={() => setIsAddCleaningServiceSheetOpen(false)}
          onAdd={handleAddServiceFromSheet}
        />
      )}
      {isAddFloorcareSheetOpen && (
        <AddFloorcareServiceSheet
          isOpen={isAddFloorcareSheetOpen}
          onClose={() => setIsAddFloorcareSheetOpen(false)}
          onAdd={handleAddServiceFromSheet}
        />
      )}
      {isViewServiceSheetOpen && selectedService && (
        <ViewCleaningServiceSheet
          isOpen={isViewServiceSheetOpen}
          onClose={() => setIsViewServiceSheetOpen(false)}
          service={{
            id: selectedService.id,
            name: selectedService.name,
            description: selectedService.description || "",
            type: selectedService.type,
            // Determine pricing method based on available data
            pricingMethod:
              selectedService.pricingMethod ||
              (selectedService.squareFootage > 0 &&
              selectedService.price > 0 &&
              !selectedService.hours
                ? "squareFootage"
                : "hourly"),
            frequency: selectedService.frequency,
            serviceDays: selectedService.serviceDays || [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
            ],
            hourlyRate: selectedService.laborRate,
            estimatedHours: selectedService.hours,
            squareFootage: selectedService.squareFootage,
            // Use the actual stored ratePerSqFt value from the database
            ratePerSqFt: selectedService.ratePerSqFt,
            productionRate: selectedService.productionRate,
            overheadPercentage:
              selectedService.overheadPercentage && selectedService.overheadPercentage > 0
                ? selectedService.overheadPercentage
                : undefined,
            profitMargin:
              selectedService.profitMargin && selectedService.profitMargin > 0
                ? selectedService.profitMargin
                : undefined,
            totalPrice: selectedService.price,
          }}
        />
      )}
      {isEditCleaningServiceSheetOpen && selectedService && selectedService.type === "cleaning" && (
        <EditCleaningServiceSheet
          isOpen={isEditCleaningServiceSheetOpen}
          onClose={() => setIsEditCleaningServiceSheetOpen(false)}
          onUpdate={handleUpdateServiceFromSheet}
          service={{
            id: selectedService.id,
            name: selectedService.name,
            description: selectedService.description || "",
            type: selectedService.type,
            pricingMethod: selectedService.pricingMethod || "hourly",
            frequency: selectedService.frequency,
            serviceDays: selectedService.serviceDays || [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
            ],
            hourlyRate: selectedService.laborRate,
            estimatedHours: selectedService.hours,
            squareFootage: selectedService.squareFootage,
            // Use the actual stored ratePerSqFt value from the database
            ratePerSqFt: selectedService.ratePerSqFt || 0,
            productionRate: selectedService.productionRate,
            overheadPercentage: selectedService.overheadPercentage || 0,
            profitMargin: selectedService.profitMargin || 0,
            totalPrice: selectedService.price,
          }}
          bidId={bidId}
        />
      )}
      {isEditFloorcareServiceSheetOpen &&
        selectedService &&
        selectedService.type === "floorcare" && (
          <EditFloorcareServiceSheet
            isOpen={isEditFloorcareServiceSheetOpen}
            onClose={() => setIsEditFloorcareServiceSheetOpen(false)}
            onUpdate={handleUpdateServiceFromSheet}
            service={{
              id: selectedService.id,
              name: selectedService.name,
              description: selectedService.description || "",
              type: selectedService.type,
              pricingMethod: selectedService.pricingMethod || "hourly",
              frequency: selectedService.frequency,
              serviceDays: selectedService.serviceDays || [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
              ],
              hourlyRate: selectedService.laborRate,
              estimatedHours: selectedService.hours,
              squareFootage: selectedService.squareFootage,
              // Use the actual stored ratePerSqFt value from the database
              ratePerSqFt: selectedService.ratePerSqFt || 0,
              productionRate: selectedService.productionRate,
              overheadPercentage: selectedService.overheadPercentage || 0,
              profitMargin: selectedService.profitMargin || 0,
              totalPrice: selectedService.price,
            }}
            bidId={bidId}
          />
        )}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Pricing Calculator</h2>
          <p className="text-gray-500">Calculate bid pricing for cleaning and floorcare services</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_350px]">
        <div className="space-y-6">
          <Card className="p-6">
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as "cleaning" | "floorcare")}
            >
              <TabsList className="mb-4">
                <TabsTrigger value="cleaning">Cleaning Services</TabsTrigger>
                <TabsTrigger value="floorcare">Floorcare Services</TabsTrigger>
              </TabsList>

              <TabsContent value="cleaning" className="space-y-4">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-md font-medium">Cleaning Services List</h3>
                  <Button onClick={() => setIsAddCleaningServiceSheetOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" /> Add Service
                  </Button>
                </div>
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Frequency</TableHead>
                        <TableHead>Service Days</TableHead>
                        <TableHead>Monthly Price</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {services.filter((service) => service.type === "cleaning").length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="py-4 text-center text-gray-500">
                            No cleaning services added yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        services
                          .filter((service) => service.type === "cleaning")
                          .map((service) => (
                            <TableRow key={service.id}>
                              <TableCell>{service.name}</TableCell>
                              <TableCell>
                                {service.description || "General cleaning service"}
                              </TableCell>
                              <TableCell>{formatFrequencyForDisplay(service.frequency)}</TableCell>
                              <TableCell>
                                {service.serviceDays ? service.serviceDays.join(", ") : "Mon-Fri"}
                              </TableCell>
                              <TableCell>${service.price.toFixed(2)}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedService(service);
                                      setIsViewServiceSheetOpen(true);
                                    }}
                                  >
                                    <FileText className="h-4 w-4 text-blue-500" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedService(service);
                                      setIsEditCleaningServiceSheetOpen(true);
                                    }}
                                  >
                                    <Edit className="h-4 w-4 text-gray-500" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveService(service.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
                <div className="mt-4 rounded-md bg-gray-50 p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Total Monthly Price:</span>
                    <span className="text-lg font-bold">${totalCleaningPrice.toFixed(2)}</span>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="floorcare" className="space-y-4">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-md font-medium">Floorcare Services List</h3>
                  <Button onClick={() => setIsAddFloorcareSheetOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" /> Add Service
                  </Button>
                </div>
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Frequency</TableHead>
                        <TableHead>Service Days</TableHead>
                        <TableHead>Monthly Price</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {services.filter((service) => service.type === "floorcare").length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="py-4 text-center text-gray-500">
                            No floorcare services added yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        services
                          .filter((service) => service.type === "floorcare")
                          .map((service) => (
                            <TableRow key={service.id}>
                              <TableCell>{service.name}</TableCell>
                              <TableCell>
                                {service.description || "Specialized floorcare service"}
                              </TableCell>
                              <TableCell>{formatFrequencyForDisplay(service.frequency)}</TableCell>
                              <TableCell>
                                {service.serviceDays
                                  ? service.serviceDays.join(", ")
                                  : "As scheduled"}
                              </TableCell>
                              <TableCell>${service.price.toFixed(2)}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedService(service);
                                      setIsViewServiceSheetOpen(true);
                                    }}
                                  >
                                    <FileText className="h-4 w-4 text-blue-500" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedService(service);
                                      setIsEditFloorcareServiceSheetOpen(true);
                                    }}
                                  >
                                    <Edit className="h-4 w-4 text-gray-500" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveService(service.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
                <div className="mt-4 rounded-md bg-gray-50 p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Total Monthly Price:</span>
                    <span className="text-lg font-bold">${totalFloorcarePrice.toFixed(2)}</span>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="mb-4 text-lg font-semibold">Pricing Summary</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total Cleaning Price:</span>
                <span className="text-lg font-semibold">${totalCleaningPrice.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total Floorcare Price:</span>
                <span className="text-lg font-semibold">${totalFloorcarePrice.toFixed(2)}</span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-gray-600">Recommended</span>
                  <span className="text-gray-600">Monthly Price:</span>
                </div>
                <span className="text-xl font-bold text-blue-600">
                  ${recommendedMonthlyPrice.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-gray-600">Recommended</span>
                  <span className="text-gray-600">Annual Price:</span>
                </div>
                <span className="text-xl font-bold text-blue-600">
                  ${recommendedAnnualPrice.toFixed(2)}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
